import { Component, ErrorHandler, OnInit, PLATFORM_ID, inject, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { HeaderComponent } from "./components/global-header/header/header.component";
import { SidebarComponent } from "./components/auth/sidebar/sidebar.component";
import { MatSidenavModule } from '@angular/material/sidenav';
import { HeadService, HttpService, InterceptorService, HeadAnalyticsService, TagManagerService } from './services';
import { SeoService } from './services/seo.service';
import { CookieConsentComponent } from './components/global-component/cookie-consent/cookie-consent.component';
import { scrollAppToTop } from './services/global/app-scroll';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent, SidebarComponent, MatSidenavModule, CookieConsentComponent],
  providers: [HttpService, InterceptorService],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'Cheapticket.in';
  private seoService = inject(SeoService);
  private router = inject(Router);
  private errorHandler = inject(ErrorHandler);
  private platformId = inject(PLATFORM_ID);

  constructor(
    private head: HeadService,
    private headAnalytics: HeadAnalyticsService,
    private tagManager: TagManagerService
  ) {
    this.head.applyHead();
    this.seoService.resetSeoTags(); // apply defaults as soon as app loads (home page)
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Start every new page at the top. Only the path counts, so query-param updates
      // (filters, sort, pagination) keep their scroll position. Back/forward (popstate) also
      // keeps it, so returning to a long results list doesn't throw the user back to the top.
      let lastPath = '';
      let isPopstate = false;
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          isPopstate = event.navigationTrigger === 'popstate';
          return;
        }
        if (!(event instanceof NavigationEnd)) return;
        const path = (event.urlAfterRedirects || event.url).split(/[?#]/)[0];
        if (lastPath && path !== lastPath && !isPopstate) {
          scrollAppToTop();
        }
        lastPath = path;
      });
      // No login dialog on landing: it covered the consent banner and pulled the auth
      // carousel image onto the critical path. Login opens from the header, and guests
      // are offered an account on the booking confirmation page instead.
    }
  }

  @HostListener('window:offline')
  onOffline() {
    if (isPlatformBrowser(this.platformId)) {
      this.router.navigate(['/error'], { queryParams: { type: 'offline' } });
    }
  }

  @HostListener('window:online')
  onOnline() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.router.url.includes('/error') && this.router.url.includes('type=offline')) {
        this.router.navigate(['/']);
      }
    }
  }

  /**
   * R3-06. This used to navigate to /error with `title` and `message` in the
   * QUERY STRING — diagnostics the error page never reads (it takes both from
   * `window.history.state`), so the values were dead on arrival while the raw
   * rejection text still landed in the address bar, in browser history, and in
   * the full page URL that GA4's automatic page_view ships to Google.
   *
   * Rejections now go through the single global policy in GlobalErrorHandler,
   * which logs them, ignores the ones the interceptor already owns, and does
   * not tear the app down (R3-05).
   */
  @HostListener('window:unhandledrejection', ['$event'])
  onUnhandledRejection(event: PromiseRejectionEvent) {
    if (isPlatformBrowser(this.platformId) && event.reason) {
      this.errorHandler.handleError(event.reason);
    }
  }
}
